import { ref, Ref, watch } from 'vue';
import { IUploadRecord } from '../MassUpload.types';
import { HasRenditionFileByDocumentId } from './useVersionsDiff';
import { isDefined } from 'utils';

type UploadRecordCondition = (uploadRecord: IUploadRecord) => boolean;

export type RecordsWithoutFile = {
  missingFile: string[];
  changedMetadata: string[];
  emptyFiles: string[];
};

export function useMissingFilesChecker(
  uploadRecords: Ref<IUploadRecord[]>,
  hasRenditionFileByDocumentId: Ref<HasRenditionFileByDocumentId>,
) {
  const recordsWithoutFile = ref<RecordsWithoutFile>();

  watch(uploadRecords, setRecordsWithoutFile, { deep: true });

  /**
   * @returns an object containing arrays of `documentId`s of records that are
   * missing a file or have an empty file attached divided into three
   * categories.
   */
  function setRecordsWithoutFile(): void {
    recordsWithoutFile.value = {
      missingFile: getDocumentIdsOfMatchingRecords(
        uploadRecords.value,
        isMissingFile,
      ),
      changedMetadata: getDocumentIdsOfMatchingRecords(
        uploadRecords.value,
        isMetadataChanged,
      ),
      emptyFiles: getDocumentIdsOfMatchingRecords(
        uploadRecords.value,
        isFileEmpty,
      ),
    };
  }

  function areRecordsWithoutFilePresent(): boolean {
    return !!(
      recordsWithoutFile.value &&
      Object.values(recordsWithoutFile.value).some(
        (category) => category.length > 0,
      )
    );
  }

  /**
   * @returns file paths of records that are missing files and are not
   * metadata-only updates so that they can be saved for reporting purposes.
   */
  function getFilePathsOfRecordsWithMissingFiles(): string[] {
    return uploadRecords.value
      .filter((record) =>
        [
          ...(recordsWithoutFile.value?.emptyFiles ?? []),
          ...(recordsWithoutFile.value?.missingFile ?? []),
        ].includes(record.metadata!.documentId),
      )
      .map((record) => record.metadata?.Filepath)
      .filter(isDefined);
  }

  /**
   * Matches records that are missing the main file or the rendition file if
   * it's expected and are uploaded for the first time i.e. these are not
   * metadata updates. These records will be dropped from the upload.
   */
  function isMissingFile(uploadRecord: IUploadRecord): boolean {
    const isNoMainFileAndNoPreviousVersion =
      !uploadRecord.file && !uploadRecord.hasPreviousVersion;
    return (
      isNoMainFileAndNoPreviousVersion ||
      isNoRenditionFileAndNoPreviousRendition(uploadRecord)
    );
  }
  /**
   * Matches records that are missing the main file or the rendition file if
   * it's expected, but a previous version of these files exists. These records
   * will be treated as metadata updates.
   */
  function isMetadataChanged(uploadRecord: IUploadRecord): boolean {
    return (
      !!(
        (!uploadRecord.file || isMissingRenditionFile(uploadRecord)) &&
        uploadRecord.hasPreviousVersion
      ) && !isNoRenditionFileAndNoPreviousRendition(uploadRecord)
    );
  }
  /**
   * Matches records that have an empty main file or rendition file attached.
   * These records will be dropped from the upload.
   */
  function isFileEmpty(uploadRecord: IUploadRecord): boolean {
    return (
      (uploadRecord.file && !uploadRecord.file.size) ||
      isEmptyRenditionFile(uploadRecord)
    );
  }

  /**
   * Checks if the previous version of the document (if exists) has a rendition
   * file (it could have been uploaded without a rendition file previously).
   */
  function hasPreviousRenditionVersion(uploadRecord: IUploadRecord): boolean {
    return !!(
      uploadRecord.metadata?.documentId &&
      hasRenditionFileByDocumentId.value[uploadRecord.metadata.documentId]
    );
  }

  /**
   * The CSV contained a record for the rendition file but the previous
   * version of the document did not contain a rendition file. If a rendition
   * file is not provided in this upload, this `uploadRecord` cannot be
   * treated as a metadata-only update because the original rendition does
   * not exist (it has to be dropped from the upload).
   */
  function isNoRenditionFileAndNoPreviousRendition(
    uploadRecord: IUploadRecord,
  ): boolean {
    return (
      isMissingRenditionFile(uploadRecord) &&
      !hasPreviousRenditionVersion(uploadRecord)
    );
  }

  /**
   * Checks if the rendition file is expected (the upload record has a
   * `renditionFile` property after CSV parsing) and if yes, checks if the
   * rendition file is missing.
   * @returns true if the rendition file is expected and missing.
   */
  function isMissingRenditionFile(uploadRecord: IUploadRecord): boolean {
    return !!(uploadRecord.renditionFile && !uploadRecord.renditionFile.file);
  }

  /**
   * Checks if the rendition file is attached to the upload record and if yes,
   * checks if the rendition file is empty.
   * @returns true if the rendition file is expected and empty.
   */
  function isEmptyRenditionFile(uploadRecord: IUploadRecord): boolean {
    return !!(
      uploadRecord.renditionFile?.file && !uploadRecord.renditionFile.file.size
    );
  }

  function getDocumentIdsOfMatchingRecords(
    uploadRecords: IUploadRecord[],
    condition: UploadRecordCondition,
  ): string[] {
    return uploadRecords
      .filter(condition)
      .map((record) => record.metadata!.documentId);
  }

  /**
   * Filtering out records that should be dropped from the upload and ensuring
   * that no files are added to records that were clasified as metadata-only
   * updates (if the main file or the rendition file was not attached and the
   * other was present, it has to be removed manually before the upload).
   */
  function cleanUploadRecords(): void {
    uploadRecords.value = uploadRecords.value.filter(
      (record) => !isMissingFile(record) && !isFileEmpty(record),
    );
    uploadRecords.value = uploadRecords.value.map((record) => {
      if (
        recordsWithoutFile.value?.changedMetadata.includes(
          record.metadata!.documentId,
        )
      ) {
        delete record.file;
        delete record.renditionFile?.file;
      }
      return record;
    });
  }

  return {
    recordsWithoutFile,
    areRecordsWithoutFilePresent,
    isMetadataChanged,
    getFilePathsOfRecordsWithMissingFiles,
    removeInvalidRecords: cleanUploadRecords,
  };
}
